import {
    type ViewStyle,
    StyleSheet
} from "react-native";

export type StateColorType = "primary" | "danger" | "warning" | "neutral" | "success" | "info";

export type StateColorKey = keyof NCoreUIKit.StateSystemColors["overlay"]["focus"];

export type TypedStateColorKey = Exclude<StateColorKey, "default">;

export type StateColorKeyConstantType = {
    type: StateColorType;
    isDisabled?: boolean;
};

export type FocusRingStyleConstantType = {
    colors: NCoreUIKit.ActivePalette["colors"];
    borders: NCoreUIKit.Borders;
    colorKey?: StateColorKey;
    isFocusVisible: boolean;
    isInset?: boolean;
};

export type FieldFocusStyleConstantType = {
    colors: NCoreUIKit.ActivePalette["colors"];
    borders: NCoreUIKit.Borders;
    isFocused: boolean;
    isError?: boolean;
};

export const STATE_COLOR_KEYS: Record<StateColorType, TypedStateColorKey> = {
    neutral: "neutral",
    primary: "primary",
    success: "success",
    warning: "warning",
    danger: "error",
    info: "info"
};

export const getStateColorKey = ({
    isDisabled = false,
    type
}: StateColorKeyConstantType): TypedStateColorKey => {
    if(isDisabled) {
        return "neutral";
    }

    return STATE_COLOR_KEYS[type];
};

export const getFocusRingStyle = ({
    colorKey = "default",
    isInset = false,
    isFocusVisible,
    borders,
    colors
}: FocusRingStyleConstantType): ViewStyle => {
    if(!isFocusVisible) {
        return {};
    }

    return {
        ...stylesheet.focusRing,
        outlineColor: colors.system.state.overlay.focus[colorKey],
        outlineOffset: isInset ? -borders.focus : borders.focus,
        outlineWidth: borders.focus
    };
};

export const getFieldFocusStyle = ({
    isError = false,
    isFocused,
    borders,
    colors
}: FieldFocusStyleConstantType): ViewStyle => {
    if(!isFocused && !isError) {
        return {};
    }

    if(!isFocused) {
        return {
            borderColor: colors.content.border.danger
        };
    }

    return {
        boxShadow: [
            {
                color: isError ? colors.system.container.error : colors.system.container.primary,
                spreadDistance: borders.halo,
                offsetX: 0,
                offsetY: 0
            }
        ],
        borderColor: isError ? colors.content.border.danger : colors.content.border.emphasized
    };
};

const stylesheet = StyleSheet.create({
    focusRing: {
        outlineStyle: "solid"
    }
});
